import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds, useMasterData, useMutation } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient from '../../utils/api';

interface DeadFishFormData {
  pond_id: string;
  death_date: string;
  fish_species: string;
  dead_weight_kg?: number;
  estimated_count?: number;
  cause?: string;
  remarks?: string;
  location_id?: string;
}

interface DeadFishLog {
  id: string;
  pond_id: string;
  death_date: string;
  fish_species?: string;
  dead_weight_kg?: number;
  estimated_count?: number;
  cause?: string;
  remarks?: string;
  created_at: string;
  created_by: string;
}

interface PondDeadFishData {
  id: string;
  name: string;
  pond_type: string;
  fish_species: string;
  dead_weight_kg: string;
  estimated_count: string;
  cause: string;
  remarks: string;
  expanded: boolean;
}

const DeadFishForm: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [deathDate, setDeathDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [pondsData, setPondsData] = useState<PondDeadFishData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { data: fishSpecies } = useMasterData('fish_species', selectedLocation?.id || null);
  const { mutate: createDeadFishLog, loading: creating } = useMutation<DeadFishLog, DeadFishFormData>();

  const commonCauses = [
    'Disease',
    'Water Quality Issues',
    'Temperature Stress',
    'Predation',
    'Oxygen Depletion',
    'Natural Mortality',
    'Unknown',
    'Other'
  ];

  useEffect(() => {
    if (ponds) {
      setPondsData(ponds.map(pond => ({
        id: pond.id,
        name: pond.name,
        pond_type: pond.pond_type,
        fish_species: '',
        dead_weight_kg: '',
        estimated_count: '',
        cause: '',
        remarks: '',
        expanded: false
      })));
    }
  }, [ponds]);

  const updatePond = (pondId: string, field: keyof PondDeadFishData, value: string | boolean) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, [field]: value } : pond
    ));
  };

  const togglePondExpansion = (pondId: string) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, expanded: !pond.expanded } : pond
    ));
  };

  const handleVoiceInput = (pondId: string) => {
    alert(`Voice input for ${pondId} - Feature would integrate with device microphone`);
  };

  // Bulk actions for efficiency
  const copyToAllPonds = (field: keyof PondDeadFishData, value: string) => {
    setPondsData(pondsData.map(pond => ({ ...pond, [field]: value })));
  };

  const clearAllPonds = () => {
    setPondsData(pondsData.map(pond => ({ 
      ...pond, 
      fish_species: '', 
      dead_weight_kg: '', 
      estimated_count: '',
      cause: '',
      remarks: '' 
    })));
  };

  const handleSubmit = async () => {
    const validEntries = pondsData.filter(pond => 
      (pond.dead_weight_kg || pond.estimated_count) && pond.fish_species
    );
    
    if (validEntries.length === 0) {
      alert('Please fill mortality data (weight/count) and select species for at least one pond');
      return;
    }

    try {
      setLoading(true);
      const promises = validEntries.map(pond => 
        createDeadFishLog('/dead-fish-logs', {
          pond_id: pond.id,
          death_date: deathDate,
          fish_species: pond.fish_species,
          dead_weight_kg: pond.dead_weight_kg ? parseFloat(pond.dead_weight_kg) : undefined,
          estimated_count: pond.estimated_count ? parseInt(pond.estimated_count) : undefined,
          cause: pond.cause || undefined,
          remarks: pond.remarks || undefined,
          location_id: selectedLocation?.id,
        })
      );
      
      await Promise.all(promises);
      alert(`✅ Successfully recorded mortality data for ${validEntries.length} ponds on ${deathDate}`);
      
      // Reset form
      setPondsData(pondsData.map(pond => ({ 
        ...pond, 
        fish_species: '', 
        dead_weight_kg: '', 
        estimated_count: '',
        cause: '',
        remarks: '' 
      })));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTotalWeight = () => {
    return pondsData.reduce((sum, pond) => sum + (parseFloat(pond.dead_weight_kg) || 0), 0).toFixed(1);
  };

  const getTotalCount = () => {
    return pondsData.reduce((sum, pond) => sum + (parseInt(pond.estimated_count) || 0), 0);
  };

  const getFilledPondsCount = () => {
    return pondsData.filter(pond => 
      (pond.dead_weight_kg || pond.estimated_count) && pond.fish_species
    ).length;
  };

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-800">💀 Dead Fish Log - EOD</h1>
        </div>
        
        {/* Date Selection */}
        <div className="flex items-center gap-2 mb-3">
          <input
            type="date"
            value={deathDate}
            onChange={(e) => setDeathDate(e.target.value)}
            className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={clearAllPonds}
            className="bg-red-100 text-red-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
          >
            <span className="text-xs">🔄</span>
            Clear All
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="bg-red-50 p-2 rounded-lg">
          <div className="flex justify-between text-xs text-red-700 mb-1">
            <span>Progress</span>
            <span>{getFilledPondsCount()}/{pondsData.length} ponds completed</span>
          </div>
          <div className="w-full bg-red-200 rounded-full h-2">
            <div 
              className="bg-red-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(getFilledPondsCount() / pondsData.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Ponds List */}
      <div className="p-4 space-y-3">
        {pondsData.map((pond) => (
          <div key={pond.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Pond Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{pond.name}</h3>
                  <p className="text-sm text-gray-500">{pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}</p>
                </div>
                <button
                  onClick={() => togglePondExpansion(pond.id)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  {pond.expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Entry Fields */}
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Dead Weight (kg)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={pond.dead_weight_kg}
                    onChange={(e) => updatePond(pond.id, 'dead_weight_kg', e.target.value)}
                    placeholder="0.0"
                    className="w-full p-3 border border-gray-300 rounded-lg text-center text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Estimated Count
                  </label>
                  <input
                    type="number"
                    value={pond.estimated_count}
                    onChange={(e) => updatePond(pond.id, 'estimated_count', e.target.value)}
                    placeholder="0"
                    className="w-full p-3 border border-gray-300 rounded-lg text-center text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Species Selection - Always Visible */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Fish Species *
                </label>
                <select
                  value={pond.fish_species}
                  onChange={(e) => updatePond(pond.id, 'fish_species', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Species *</option>
                      {fishSpecies?.map((species) => (
                        <option key={species.id} value={species.name}>
                          {species.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ Species selection is required for proper inventory tracking
                    </p>
                  </div>

              {/* Expanded Fields */}
              {pond.expanded && (
                <div className="space-y-3 border-t border-gray-100 pt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Cause
                    </label>
                    <select
                      value={pond.cause}
                      onChange={(e) => updatePond(pond.id, 'cause', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Select Cause (Optional)</option>
                      {commonCauses.map((cause) => (
                        <option key={cause} value={cause}>
                          {cause}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-700">
                        Remarks (Optional)
                      </label>
                      <button
                        onClick={() => handleVoiceInput(pond.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <span className="text-sm">🎤</span>
                      </button>
                    </div>
                    <textarea
                      value={pond.remarks}
                      onChange={(e) => updatePond(pond.id, 'remarks', e.target.value)}
                      placeholder="Symptoms observed, treatment attempted, environmental conditions, etc."
                      rows={2}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Summary & Submit */}
      <div className="p-4 bg-white border-t border-gray-200 sticky bottom-0">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-red-50 p-3 rounded-lg text-center">
            <div className="text-xs text-red-600 font-medium">Total Dead Weight</div>
            <div className="text-lg font-bold text-red-700">{getTotalWeight()} kg</div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg text-center">
            <div className="text-xs text-gray-600 font-medium">Total Dead Count</div>
            <div className="text-lg font-bold text-gray-700">{getTotalCount()}</div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading || creating}
          className="w-full bg-red-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading || creating ? <LoadingSpinner size="sm" /> : <span className="text-lg">💾</span>}
          Submit EOD Mortality ({getFilledPondsCount()}/{pondsData.length})
        </button>
      </div>
    </div>
  );
};

export default DeadFishForm;